import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api, { fmtIDR } from "../lib/api";
import { fmtUSDT } from "../lib/currency";

const errOf = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);

export default function BinancePayAutoPanel({ o, isBuyer, reload }) {
  const [params, setParams] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [pay, setPay] = useState(null);
  const handled = useRef(false);
  const b = o.binancepay || {};
  const live = pay || (b.prepay_id && b.expire_time > Date.now() ? b : null);
  const waiting = o.status === "waiting_payment";

  const check = async (silent) => {
    setBusy(true);
    try {
      const r = await api.post(`/orders/${o.id}/binance/check`);
      if (r.data.ok) { toast.success("Pembayaran Binance Pay terverifikasi. Pesanan diproses."); reload(); }
      else if (!silent) toast.info(`Belum lunas di Binance Pay (status: ${r.data.binance_status || "-"}).`);
    } catch (e) { if (!silent) toast.error(errOf(e, "Gagal cek status")); } finally { setBusy(false); }
  };

  useEffect(() => {
    const flag = params.get("binance");
    if (!flag || handled.current || !isBuyer) { return; }
    handled.current = true;
    setParams({}, { replace: true });
    if (flag === "cancel") { toast.info("Pembayaran Binance Pay dibatalkan. Anda bisa mencoba lagi sebelum batas waktu."); return; }
    check(true);
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy(true);
    try { const r = await api.post(`/orders/${o.id}/binance/create`); setPay(r.data); }
    catch (e) { toast.error(errOf(e, "Gagal membuat pembayaran Binance Pay")); } finally { setBusy(false); }
  };

  return (
    <div data-testid="binance-auto-panel" className="border border-[#D4AF37]/30 rounded-sm p-3 mb-3 space-y-2">
      <div className="flex justify-between text-xs"><span className="text-[#A8A8AD]">Total (IDR)</span><span className="text-[#F5F3ED]">{fmtIDR(o.amount_due_idr ?? o.total_idr)}</span></div>
      <div className="flex justify-between text-xs"><span className="text-[#A8A8AD]">Dibayar via Binance Pay</span><span data-testid="bp-amount" className="text-[#D4AF37] font-bold text-sm">{o.amount_due_usd != null ? fmtUSDT(o.amount_due_usd) : "-"}</span></div>
      {o.fx_rate && <div className="text-[10px] text-[#A8A8AD] text-right">Kurs terkunci 1 USDT = {fmtIDR(o.fx_rate)}</div>}
      {b.transaction_id && <div data-testid="bp-verified" className="text-xs text-[#35C78A]">Terverifikasi Binance Pay · {b.transaction_id}</div>}
      {b.needs_review && <div className="text-[11px] text-[#F06464]">Pembayaran sedang ditinjau Admin.</div>}
      {isBuyer && waiting && !live && (
        <button data-testid="bp-pay-btn" onClick={start} disabled={busy} className="w-full rounded-full py-2.5 font-bold text-sm bg-[#F0B90B] hover:bg-[#f8d12f] text-[#111] disabled:opacity-50 transition-colors">
          {busy ? "Memproses..." : "Bayar dengan Binance Pay"}
        </button>
      )}
      {isBuyer && waiting && live && (
        <div data-testid="bp-checkout" className="flex flex-col items-center gap-2 pt-1">
          {live.qr_content && <div className="bg-white p-2 rounded-md"><QRCodeSVG data-testid="bp-qr" value={live.qr_content} size={180} /></div>}
          <div className="text-[11px] text-[#A8A8AD] text-center">Scan dengan aplikasi Binance atau buka halaman checkout Binance Pay.</div>
          {live.checkout_url && <a data-testid="bp-checkout-link" href={live.checkout_url} target="_blank" rel="noopener noreferrer" className="w-full text-center rounded-full py-2 font-bold text-sm bg-[#F0B90B] hover:bg-[#f8d12f] text-[#111] transition-colors">Buka Checkout Binance Pay</a>}
        </div>
      )}
      {waiting && (b.prepay_id || pay) && (
        <button data-testid="bp-check-btn" onClick={() => check(false)} disabled={busy} className="w-full border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#101114] rounded-full py-2 text-sm transition-colors disabled:opacity-50">Cek Status Pembayaran</button>
      )}
      {b.last_error && waiting && <div className="text-[11px] text-[#F06464]">{b.last_error}</div>}
    </div>
  );
}
